import { Alert, Button, Card, Divider, List, Popconfirm, Result, Space, Spin, Switch, Tag, Typography, notification } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { KeyRound, ShieldCheck, Smartphone, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import PageContentWrapper from "../../components/PageContentWrapper";
import ReauthenticateModal from "../../components/security/ReauthenticateModal";
import SecurityEnrollmentModal from "../../components/security/SecurityEnrollmentModal";
import { useAccountSecurity } from "../../components/security/useAccountSecurity";
import { signInAgain } from "../../utils/mfa-step-up";
import { isPasskeySupported } from "../../utils/webauthn";

// A settings page is read top to bottom, one decision at a time. At the full
// width of the shell each row stretches the eye across the screen and the
// sparse ones look empty, so the column is capped at a comfortable measure and
// centred. The header is capped with it, to keep the title over the cards.
const CONTENT_WIDTH = 760;
const centeredColumn = { maxWidth: CONTENT_WIDTH, margin: "0 auto" };

export default function AccountSecurity() {
    const navigate = useNavigate();
    const {
        mfaRequired, projectName, intendedPath, overview, loading, loadFailed, loadOverview,
        factors, hasTotp, federated, providers,
        enrollmentMethod, enrolling, enrolled, startEnrollment, closeEnrollment, completeEnrollment,
        countdown, cancelCountdown, setPromptPreference, removeFactor, renameFactor, reauth,
    } = useAccountSecurity((kind, message, description) => notification[kind]({ message, description }));
    const onlyFactor = factors.length === 1;

    const header = (
        <div style={centeredColumn}>
        <PageHeader
            title="Account security"
            subTitle="Manage how you sign in to Microtica."
            breadcrumb={{
                items: [
                    { title: <span onClick={() => navigate("/projects", { state: { showProjectsList: true } })} style={{ cursor: "pointer" }}>Projects</span> },
                    { title: "Account security" }
                ]
            }}
        />
        </div>
    );

    if (mfaRequired && enrolled) {
        return (
            <PageContentWrapper size="large" header={header}>
                <div style={centeredColumn}>
                <Card>
                    <Result
                        status="success"
                        title="Two-step verification is on"
                        subTitle={`One last step: sign in again so we can verify it. You'll come straight back to ${projectName || "your project"}.`}
                        extra={[
                            <Button key="sign-in" type="primary" onClick={() => signInAgain(intendedPath)}>
                                Sign in again{countdown !== undefined ? ` (${countdown})` : ""}
                            </Button>,
                            ...(countdown !== undefined
                                ? [<Button key="cancel" onClick={cancelCountdown}>Cancel</Button>]
                                : [])
                        ]}
                    />
                </Card>
            </div>
            </PageContentWrapper>
        );
    }

    if (loading) {
        return (
            <PageContentWrapper size="large" header={header}>
                <div style={centeredColumn}>
                <Card><div style={{ textAlign: "center", padding: 32 }}><Spin /></div></Card>
            </div>
            </PageContentWrapper>
        );
    }

    if (loadFailed || !overview) {
        return (
            <PageContentWrapper size="large" header={header}>
                <div style={centeredColumn}>
                <Alert
                    type="error"
                    showIcon
                    message="Account security couldn't be loaded"
                    description="No security settings were changed."
                    action={<Button onClick={() => loadOverview()}>Try again</Button>}
                />
            </div>
            </PageContentWrapper>
        );
    }

    return (
        <PageContentWrapper size="large" header={header}>
                <div style={centeredColumn}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
                {mfaRequired && (
                    <Alert
                        type="warning"
                        showIcon
                        message={`${projectName || "This project"} requires two-step verification`}
                        description={federated
                            ? `${providers} manages this account's sign-in, and Microtica cannot verify its two-step verification for a project policy yet. This account cannot open the project while the policy is on.`
                            : "Set up an authenticator app or a passkey, then sign in again to continue."}
                        action={federated ? undefined : <Button type="primary" size="small" onClick={() => signInAgain(intendedPath)}>Sign in again</Button>}
                    />
                )}

                <Alert
                    type="info"
                    showIcon
                    icon={<ShieldCheck size={18} />}
                    message={federated ? `Sign-in security is managed by ${providers}` : "Add an authenticator app or a passkey"}
                    description={federated
                        ? `You sign in through ${providers}, so its passkeys and two-step verification protect that login. Microtica cannot add a separate authenticator app or passkey to this account in this release.`
                        : "A passkey signs you in without a password. An authenticator app adds a six-digit code after your password."}
                />

                <Card title="Sign-in methods">
                    {federated ? (
                        <List>
                            <List.Item extra={<Tag color="blue">Managed externally</Tag>}>
                                <List.Item.Meta
                                    avatar={<ShieldCheck size={20} />}
                                    title={providers}
                                    description={`Continue using ${providers} on the Microtica sign-in page. Manage passkeys and two-step verification in your ${providers} account.`}
                                />
                            </List.Item>
                        </List>
                    ) : (
                        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                            {onlyFactor && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message="You have only one sign-in method. If you lose it you'll need to contact support to regain access to projects that require two-step verification. Add a second method now."
                                />
                            )}
                            {factors.length > 0 && (
                                <List
                                    dataSource={factors}
                                    renderItem={factor => (
                                        <List.Item
                                            actions={[
                                                <Popconfirm
                                                    key="remove"
                                                    title={onlyFactor ? "This is your only sign-in method." : "Remove this sign-in method?"}
                                                    description={onlyFactor
                                                        ? "Removing it will lock you out of projects that require two-step verification until you add another."
                                                        : "You can add it again later."}
                                                    okText="Remove"
                                                    okButtonProps={{ danger: true }}
                                                    onConfirm={() => removeFactor(factor)}
                                                >
                                                    <Button type="text" danger icon={<Trash2 size={16} />}>Remove</Button>
                                                </Popconfirm>
                                            ]}
                                        >
                                            <List.Item.Meta
                                                avatar={factor.type === "totp" ? <Smartphone size={20} /> : <KeyRound size={20} />}
                                                title={factor.type === "passkey"
                                                    ? <Typography.Text editable={{ maxLength: 80, onChange: name => renameFactor(factor, name) }}>
                                                        {factor.label || "Passkey"}
                                                    </Typography.Text>
                                                    : factor.label || "Authenticator app"}
                                                description={factor.createdAt ? `Added ${new Date(factor.createdAt).toLocaleDateString()}` : undefined}
                                            />
                                        </List.Item>
                                    )}
                                />
                            )}
                            {factors.length > 0 && <Divider style={{ margin: "8px 0" }} />}
                            <Typography.Title level={5} style={{ marginTop: 0 }}>Add a sign-in method</Typography.Title>
                            <Card size="small">
                                <Space align="start" style={{ justifyContent: "space-between", width: "100%" }}>
                                    <Space align="start">
                                        <Smartphone size={20} />
                                        <div>
                                            <Typography.Text strong>Authenticator app</Typography.Text>
                                            <br />
                                            <Typography.Text type="secondary">Use a six-digit code after entering your password.</Typography.Text>
                                        </div>
                                    </Space>
                                    <Button disabled={hasTotp || !overview.totpAvailable} onClick={() => startEnrollment("totp")}>
                                        {hasTotp ? "Added" : "Set up"}
                                    </Button>
                                </Space>
                            </Card>
                            <Card size="small">
                                <Space align="start" style={{ justifyContent: "space-between", width: "100%" }}>
                                    <Space align="start">
                                        <KeyRound size={20} />
                                        <div>
                                            <Typography.Text strong>Passkey</Typography.Text>
                                            <br />
                                            <Typography.Text type="secondary">Use Face ID, Touch ID, device screen lock, a password manager, or a security key. No password needed.</Typography.Text>
                                        </div>
                                    </Space>
                                    <Button disabled={!overview.passkeysAvailable || !isPasskeySupported()} onClick={() => startEnrollment("passkey")}>
                                        Add
                                    </Button>
                                </Space>
                            </Card>
                            {!isPasskeySupported() && <Typography.Text type="secondary">Open Microtica in a modern browser over HTTPS to add a passkey.</Typography.Text>}
                        </Space>
                    )}
                </Card>

                {!federated && (
                    <Card title="Reminders">
                        <div className="settings-row">
                            <div className="settings-row-text">
                                <Typography.Text strong>Remind me to add a second sign-in method</Typography.Text>
                                <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                                    {factors.length > 0
                                        ? "You already have one, so this reminder stays quiet unless you remove every method."
                                        : "Shown once after you sign in, until you add an authenticator app or a passkey."}
                                </Typography.Paragraph>
                            </div>
                            <Switch
                                checked={!overview.preferences.promptDismissed}
                                onChange={setPromptPreference}
                                aria-label="Remind me to add a second sign-in method"
                            />
                        </div>
                    </Card>
                )}
            </Space>
            {!federated && (
                <SecurityEnrollmentModal
                    open={enrolling}
                    initialMethod={enrollmentMethod}
                    onClose={closeEnrollment}
                    onComplete={completeEnrollment}
                />
            )}
            <ReauthenticateModal open={reauth.reauthenticating} onCancel={reauth.cancel} onSuccess={reauth.succeed} />
        </div>
            </PageContentWrapper>
    );
}
